import * as enabledApi from '@shared/api/enabledPlayApi';
import { DefaultClientListing } from '@shared/constants/organization/ProjectDefaults';
import DeviceTypeClientListingModel from '@shared/models/devices/DeviceTypeClientListingModel';
import DeviceTypeModel, { DeviceTypeFieldName } from '@shared/models/devices/DeviceTypeModel';
import UpdateDeviceTypeRequest from '@shared/models/devices/UpdateDeviceTypeRequest';
import OrganizationDeviceTypeModel from '@shared/models/organizations/OrganizationDeviceTypeModel';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { useState } from 'react';
import { createContainer } from 'unstated-next';
import { ensureAllLocalesExistInClientListing, getDefaultDeviceType } from '../helpers/projectUtils';
import useStateRef from '../useStateRef';

export interface DeviceTypeEditorActions {
    updateOrgDeviceType: (
        organizationId: string,
        deviceId: string,
        request: UpdateDeviceTypeRequest
    ) => Promise<IResult<DeviceTypeModel>>;
    createOrgDeviceType: (
        organizationId: string,
        request: UpdateDeviceTypeRequest
    ) => Promise<IResult<DeviceTypeModel>>;
}

function useDeviceTypeEditorContainer() {
    const [editDeviceType, setEditDeviceType, editDeviceTypeRef] = useStateRef<OrganizationDeviceTypeModel | null>(
        null
    );
    const [hasChanges, setHasChanges, hasChangesRef] = useStateRef(false);
    const [listingHasChanges, setListingHasChanges, listingHasChangesRef] = useStateRef(false);
    const [editListing, setEditListing, editListingRef] = useStateRef<DeviceTypeClientListingModel[] | null>(null);
    const [loadingListing, setLoadingListing] = useState(false);

    const initializeEditListing = async (organizationId?: string, deviceId?: string) => {
        setLoadingListing(true);
        let model: DeviceTypeClientListingModel[] = DefaultClientListing;
        if (organizationId && deviceId) {
            try {
                const result = await enabledApi.getOrgDeviceTypeClientListings(organizationId, deviceId);
                if (result.resultType === 'Ok' && result.data && result.data.length > 0) {
                    model = ensureAllLocalesExistInClientListing(result.data);
                }

                setListingHasChanges(false);
            } catch (error) {
                // Ignore errors
                console.error('Error fetching device type client listings:', error);
            } finally {
                setEditListing(model);
                setListingHasChanges(false);
                setLoadingListing(false);
            }
        }
        setEditListing(model);
    };

    const initializeEditor = (model: OrganizationDeviceTypeModel | null, organizationId?: string) => {
        if (model) {
            setEditDeviceType(model);
        } else if (organizationId) {
            setEditDeviceType(getDefaultDeviceType(organizationId));
        }
        setHasChanges(false);
    };

    const handleFieldChange = <T extends DeviceTypeFieldName>(fieldName: T, value: DeviceTypeModel[T]) => {
        setEditDeviceType((prevModel) =>
            prevModel
                ? {
                      ...prevModel,
                      [fieldName]: value,
                  }
                : null
        );
        setHasChanges(true);
    };

    const handleInputChange = <T extends DeviceTypeFieldName>(fieldName: T, value: DeviceTypeModel[T]) => {
        handleFieldChange(fieldName, value);
    };

    const createUpdateRequest = (): UpdateDeviceTypeRequest | undefined => {
        if (editDeviceTypeRef.current) {
            return editDeviceTypeRef.current as UpdateDeviceTypeRequest;
        }
    };

    const handleUpdate = async (
        actions: DeviceTypeEditorActions,
        organizationId: string,
        deviceId: string
    ): Promise<IResult<DeviceTypeModel>> => {
        try {
            const request = createUpdateRequest();
            if (!request) throw new Error('Missing request data');
            const result = await actions.updateOrgDeviceType(organizationId, deviceId, request);
            if (result.resultType === 'Ok' && result.data) {
                setEditDeviceType(result.data);
                setHasChanges(false);
            }
            return result;
        } catch (error) {
            return new UnexpectedResult<DeviceTypeModel>();
        }
    };

    const handleCreate = async (
        actions: DeviceTypeEditorActions,
        organizationId: string
    ): Promise<IResult<DeviceTypeModel>> => {
        try {
            const request = createUpdateRequest();
            if (!request) throw new Error('Missing request data');
            const result = await actions.createOrgDeviceType(organizationId, request);
            if (result.resultType === 'Ok' && result.data) {
                setEditDeviceType(result.data);
                setHasChanges(false);
            }
            return result;
        } catch (error) {
            return new UnexpectedResult<DeviceTypeModel>();
        }
    };

    const handleSave = async (
        actions: DeviceTypeEditorActions,
        organizationId: string,
        deviceId?: string
    ): Promise<IResult<DeviceTypeModel>> => {
        if (editDeviceTypeRef.current?.id && deviceId) {
            return await handleUpdate(actions, organizationId, deviceId);
        } else {
            return await handleCreate(actions, organizationId);
        }
    };

    const handleCreateAndClearEditor = async (
        actions: DeviceTypeEditorActions,
        organizationId: string
    ): Promise<IResult<DeviceTypeModel>> => {
        const result = await handleCreate(actions, organizationId);
        if (result.resultType === 'Ok') {
            setEditDeviceType(getDefaultDeviceType(organizationId));
            setHasChanges(false);
        }
        return result;
    };

    const clearEditor = (organizationId?: string) => {
        if (organizationId) {
            setEditDeviceType(getDefaultDeviceType(organizationId));
        } else {
            setEditDeviceType(null);
        }
        setHasChanges(false);
    };

    return {
        editDeviceType,
        editDeviceTypeRef,
        setEditDeviceType,
        hasChanges,
        hasChangesRef,
        initializeEditor,
        handleFieldChange,
        handleInputChange,
        handleSave,
        handleCreateAndClearEditor,
        loadingListing,
        listingHasChanges,
        listingHasChangesRef,
        setListingHasChanges,
        editListing,
        editListingRef,
        initializeEditListing,
        setEditListing,
        clearEditor,
        createUpdateRequest,
    };
}

const EditDeviceTypeContainer = createContainer(useDeviceTypeEditorContainer);

export default EditDeviceTypeContainer;
